import React, { useState, useEffect, useCallback } from "react";

import {
  getMe,
  listAiProviders,
  createAiProvider,
  updateAiProvider,
  deleteAiProvider,
  setDefaultAiProvider,
  getAiPreference,
  getAiUsage,
  saveAiPreference,
  deleteAiPreference,
} from "../../../../api";
import { useNotify } from "../../../../ToastContext";
import { usePermission } from "../../../../UserContext";
import Select from "../../../../components/Select";
import ComboInput from "../../../../components/ComboInput";
import PasswordInput from "../../../../components/PasswordInput";
import { PROVIDER_PRESETS, ENDPOINT_PRESETS, MODEL_PRESETS, getAllModelOptions, guessProviderFromEndpoint } from "../../../../utils/aiPresets";
import plusIcon from "../../../../assets/plus.svg";
import xmarkIcon from "../../../../assets/xmark.svg";
import moreIcon from "../../../../assets/more-horiz.svg";
import { isProviderReady, isManagedProvider, providerLabel } from "../../../../ai-integration/providers";

const isKeylessLocalEndpoint = (endpoint) => {
  try {
    const parsed = new URL(String(endpoint || "").trim());
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return false;
    const host = parsed.hostname.toLowerCase().replace(/^\[|\]$/g, "");
    if (host === "localhost" || host === "::1") return true;
    const octets = host.split(".").map(Number);
    return octets.length === 4
      && octets.every((octet) => Number.isInteger(octet) && octet >= 0 && octet <= 255)
      && octets[0] === 127;
  } catch {
    return false;
  }
};

const formatMicrousd = (value) => new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(Number(value || 0) / 1_000_000);

const formatResetDate = (value) => new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
}).format(new Date(value));

// ── Provider Modal ──────────────────────────────────────────────────

const ProviderModal = ({ editing, prefilled, onClose, onSaved, providerCount }) => {
  const isEdit = !!editing && editing !== "new";
  const notify = useNotify();

  const [providerType, setProviderType] = useState(() => {
    if (isEdit) return guessProviderFromEndpoint(editing.endpoint) || "custom";
    if (prefilled?.provider_type) return prefilled.provider_type;
    return "openai";
  });
  const [form, setForm] = useState(() => {
    if (isEdit) {
      return {
        name: editing.name,
        endpoint: editing.endpoint,
        api_key: "",
        default_model: editing.default_model || "",
        is_default: editing.is_default,
      };
    }
    if (prefilled) {
      const ptype = prefilled.provider_type || "openai";
      return {
        name: prefilled.name || "",
        endpoint: prefilled.endpoint || ENDPOINT_PRESETS[ptype] || ENDPOINT_PRESETS.openai,
        api_key: "",
        default_model: prefilled.default_model || "",
        is_default: prefilled.is_default != null ? !!prefilled.is_default : providerCount === 0,
      };
    }
    return {
      name: "",
      endpoint: ENDPOINT_PRESETS.openai,
      api_key: "",
      default_model: "",
      is_default: providerCount === 0,
    };
  });
  const [saving, setSaving] = useState(false);

  const set = (field) => (e) => {
    const value = typeof e === "string" ? e : (typeof e === "boolean" ? e : e.target.value);
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleProviderTypeChange = (type) => {
    setProviderType(type);
    const preset = PROVIDER_PRESETS.find((p) => p.value === type);
    const endpoint = ENDPOINT_PRESETS[type] || "";
    setForm((f) => ({
      ...f,
      name: f.name || preset?.label || "",
      endpoint: endpoint || f.endpoint,
      default_model: "",
    }));
  };

  const modelOptions = MODEL_PRESETS[providerType] || getAllModelOptions();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.endpoint.trim()) {
      notify({ type: "error", title: "Error", message: "Name and endpoint are required." }); return;
    }
    if (!isEdit && !form.api_key.trim() && !isKeylessLocalEndpoint(form.endpoint)) {
      notify({ type: "error", title: "Error", message: "API key is required unless the endpoint is local." }); return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        endpoint: form.endpoint.trim(),
        default_model: form.default_model.trim(),
        is_default: form.is_default,
      };
      if (form.api_key.trim()) payload.api_key = form.api_key.trim();
      if (isEdit) {
        await updateAiProvider(editing.id, payload);
        notify({ type: "success", title: "Updated", message: `"${form.name}" updated.` });
      } else {
        await createAiProvider(payload);
        notify({ type: "success", title: "Added", message: `"${form.name}" added.` });
      }
      onSaved();
      onClose();
    } catch (err) {
      notify({ type: "error", title: "Failed", message: String(err.message || err) });
    } finally { setSaving(false); }
  };

  return (
    <div className="gwOverlay" onClick={onClose}>
      <form className="gwModal" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit} style={{ width: 520 }}>
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">{isEdit ? `Edit: ${editing.name}` : "New AI Provider"}</div>
            <div className="gwModalSubtitle">{isEdit ? "Update provider settings." : "Pick a provider or enter custom settings."}</div>
          </div>
          <button className="gwModalClose" type="button" onClick={onClose}>
            <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
          </button>
        </div>
        <div className="gwModalBody">
          {/* Provider type selector */}
          {!isEdit && (
            <div className="gwField">
              <label className="gwLabel">Provider</label>
              <Select value={providerType} onChange={handleProviderTypeChange} options={PROVIDER_PRESETS} />
            </div>
          )}

          <div className="gwFieldRow">
            <div className="gwField" style={{ flex: 2 }}>
              <label className="gwLabel">Name</label>
              <input className="gwInput" placeholder="e.g. OpenAI Production" value={form.name} onChange={set("name")} autoFocus required />
            </div>
            <div className="gwField" style={{ flex: 1 }}>
              <label className="gwLabel">&nbsp;</label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, height: 36 }}>
                <input type="checkbox" checked={form.is_default} onChange={(e) => set("is_default")(e.target.checked)} style={{ accentColor: "var(--gw-accent)" }} />
                Default
              </label>
            </div>
          </div>

          <div className="gwField">
            <label className="gwLabel">Endpoint</label>
            <input className="gwInput" placeholder="https://api.openai.com/v1/chat/completions" value={form.endpoint} onChange={set("endpoint")} required />
            <div className="gwHint">Full URL to the chat completions endpoint.</div>
          </div>

          <div className="gwField">
            <label className="gwLabel">API Key</label>
            <PasswordInput
              placeholder={isEdit ? "Leave blank to keep current" : providerType === "ollama" ? "Not required for local" : "Enter API key"}
              value={form.api_key} onChange={set("api_key")}
            />
            <div className="gwHint">{isEdit ? "Leave blank to keep the existing key." : "Encrypted at rest and never displayed again."}</div>
          </div>

          <div className="gwField">
            <label className="gwLabel">Default Model</label>
            <ComboInput
              value={form.default_model}
              onChange={(v) => set("default_model")(v)}
              options={modelOptions}
              placeholder="Type or select a model..."
            />
            <div className="gwHint">Used when users don't specify a model override.</div>
          </div>
        </div>
        <div className="gwModalFooter">
          <button className="gwGhostBtn" type="button" onClick={onClose}>Cancel</button>
          <button className="gwPrimaryBtn" type="submit" disabled={saving}>
            {saving ? "Saving..." : isEdit ? "Save" : "Add Provider"}
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Preferences Modal ───────────────────────────────────────────────

const PreferencesModal = ({ providers, pref, onClose, onSaved }) => {
  const notify = useNotify();
  const readyProviders = providers.filter(isProviderReady);
  const defaultProvider = providers.find((p) => p.is_default) || providers[0];
  const preferredProvider = pref?.configured
    ? providers.find((p) => p.id === pref.provider_id)
    : null;
  const initialProvider = preferredProvider || defaultProvider;
  const availableProviders = initialProvider && !isProviderReady(initialProvider)
    ? [...readyProviders, initialProvider]
    : readyProviders;

  const [providerId, setProviderId] = useState(initialProvider?.id || "");
  const [model, setModel] = useState(
    isManagedProvider(initialProvider)
      ? ""
      : (pref?.configured && preferredProvider
        ? (pref.model ?? preferredProvider.default_model ?? "")
        : (initialProvider?.default_model || ""))
  );
  const [saving, setSaving] = useState(false);

  const providerOptions = availableProviders.map((p) => ({
    value: p.id,
    label: `${providerLabel(p)}${p.is_default ? " (default)" : ""}${isProviderReady(p) ? "" : " (unavailable)"}`,
  }));
  const selectedProvider = availableProviders.find((p) => p.id === providerId);
  const modelIsFixed = isManagedProvider(selectedProvider);
  const selectedProviderReady = isProviderReady(selectedProvider);

  const handleProviderChange = (id) => {
    setProviderId(id);
    const selected = availableProviders.find((p) => p.id === id);
    if (selected) setModel(isManagedProvider(selected) ? "" : (selected.default_model || ""));
  };

  const handleSave = async () => {
    if (!providerId) { notify({ type: "error", title: "Error", message: "Select a provider." }); return; }
    if (!selectedProviderReady) { notify({ type: "error", title: "Unavailable", message: "Select an available provider." }); return; }
    setSaving(true);
    try {
      await saveAiPreference({ provider_id: providerId, model: model.trim() });
      notify({ type: "success", title: "Saved", message: "AI preferences updated." });
      onSaved();
      onClose();
    } catch (err) {
      notify({ type: "error", title: "Failed", message: String(err.message || err) });
    } finally { setSaving(false); }
  };

  const handleReset = async () => {
    try {
      await deleteAiPreference();
      notify({ type: "success", title: "Reset", message: "Preferences reset to defaults." });
      onSaved();
      onClose();
    } catch (err) {
      notify({ type: "error", title: "Failed", message: String(err.message || err) });
    }
  };

  return (
    <div className="gwOverlay" onClick={onClose}>
      <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 460 }}>
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">My AI Preferences</div>
            <div className="gwModalSubtitle">Override the default provider and model for your account.</div>
          </div>
          <button className="gwModalClose" type="button" onClick={onClose}>
            <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
          </button>
        </div>
        <div className="gwModalBody">
          <div className="gwField">
            <label className="gwLabel">Provider</label>
            <Select value={providerId} onChange={handleProviderChange} options={providerOptions} placeholder="Select a provider..." />
          </div>
          {!modelIsFixed && (
            <div className="gwField">
              <label className="gwLabel">Model</label>
              <ComboInput
                value={model}
                onChange={setModel}
                options={getAllModelOptions()}
                placeholder="Type or select a model..."
              />
              <div className="gwHint">Override the provider's default model.</div>
            </div>
          )}
        </div>
        <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="gwPrimaryBtn" type="button" onClick={handleSave} disabled={saving || !selectedProviderReady}>
              {saving ? "Saving..." : "Save"}
            </button>
            <button className="gwGhostBtn" type="button" onClick={onClose}>Cancel</button>
          </div>
          {pref?.configured && (
            <button className="gwDangerBtn" type="button" onClick={handleReset} style={{ fontSize: 12 }}>
              Reset to Defaults
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Page ───────────────────────────────────────────────────────

const AiSettingsPage = () => {
  const canConfigure = usePermission("configure");
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [providers, setProviders] = useState([]);
  const [pref, setPref] = useState(null);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [proposedProvider, setProposedProvider] = useState(null);
  const [showPrefModal, setShowPrefModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);
  const [menuStyle, setMenuStyle] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [usage, setUsage] = useState(null);
  const [usageLoaded, setUsageLoaded] = useState(false);
  const notify = useNotify();

  const fetchUsage = useCallback(() => {
    setUsageLoaded(false);
    getAiUsage()
      .then((data) => setUsage(data || null))
      .catch(() => setUsage(null))
      .finally(() => setUsageLoaded(true));
  }, []);

  const fetchData = useCallback(async () => {
    const [meResult, providersResult, prefResult] = await Promise.allSettled([
      getMe(),
      listAiProviders(),
      getAiPreference(),
    ]);

    if (meResult.status === "fulfilled") {
      setIsAdmin((meResult.value?.permissions || []).includes("admin"));
    } else {
      notify({ type: "error", title: "Error", message: meResult.reason?.message || "Failed to load account" });
    }

    if (providersResult.status === "fulfilled") {
      const providerList = providersResult.value || [];
      setProviders(providerList);
      if (providerList.some(isManagedProvider)) fetchUsage();
    } else {
      notify({ type: "error", title: "Error", message: providersResult.reason?.message || "Failed to load AI providers" });
    }

    // Preference is optional display state. A transient failure must not hide
    // the provider/default data that loaded successfully or overwrite a
    // previously loaded selection.
    if (prefResult.status === "fulfilled") {
      setPref(prefResult.value || { configured: false });
    }
    setLoading(false);
  }, [fetchUsage, notify]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Refresh when the AI mutates an AI provider (delete or set-default).
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.resource === "ai_providers") fetchData();
    };
    window.addEventListener("gw-ai-resource-changed", handler);
    return () => window.removeEventListener("gw-ai-resource-changed", handler);
  }, [fetchData]);

  // AI propose-ai-provider: open the provider modal pre-filled.
  useEffect(() => {
    const handler = (e) => {
      const payload = e.detail?.payload;
      if (!payload || typeof payload !== "object") return;
      setProposedProvider(payload);
      setEditingProvider(null);
      setShowProviderModal(true);
    };
    window.addEventListener("gw-ai-propose-ai-provider-page", handler);
    return () => window.removeEventListener("gw-ai-propose-ai-provider-page", handler);
  }, []);

  const openMenu = (e, id) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setMenuStyle({ position: "fixed", top: `${Math.round(r.bottom + 4)}px`, right: `${Math.max(12, Math.round(window.innerWidth - r.right))}px` });
    setMenuOpen(menuOpen === id ? null : id);
  };

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      const menu = document.querySelector(".gwProjectMenu");
      if (menu?.contains(e.target)) return;
      setMenuOpen(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const handleSetDefault = async (p) => {
    setMenuOpen(null);
    if (!isProviderReady(p)) return;
    try {
      await setDefaultAiProvider(p.id);
      notify({ type: "success", title: "Default Set", message: `"${providerLabel(p)}" is now the default.` });
      fetchData();
    } catch (err) {
      notify({ type: "error", title: "Error", message: String(err.message || err) });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || isManagedProvider(deleteTarget)) return;
    setDeleting(true);
    try {
      await deleteAiProvider(deleteTarget.id);
      notify({ type: "success", title: "Removed", message: `"${deleteTarget.name}" deleted.` });
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      notify({ type: "error", title: "Error", message: String(err.message || err) });
    } finally { setDeleting(false); }
  };

  const defaultProvider = providers.find((p) => p.is_default) || providers[0];
  const effectiveProvider = pref?.configured ? (providers.find((p) => p.id === pref.provider_id) || defaultProvider) : defaultProvider;
  const effectiveModel = isManagedProvider(effectiveProvider)
    ? ""
    : (pref?.configured
      ? (pref.model || effectiveProvider?.default_model || "")
      : (defaultProvider?.default_model || ""));
  const hasProviders = providers.length > 0;

  if (loading) {
    return (
      <div className="gwContentGrid"><section className="gwFormStack">
        <div className="gwPageHeader"><div>
          <div className="gwPageTitle">AI Settings</div>
          <div className="gwPageSubtitle">Loading...</div>
        </div></div>
      </section></div>
    );
  }

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">AI Settings</div>
            <div className="gwPageSubtitle">
              {hasProviders ? `${providers.length} provider${providers.length !== 1 ? "s" : ""} configured` : "Connect AI providers for the editor assistant."}
            </div>
          </div>
          {isAdmin && (
            <button className="gwPrimaryBtn" type="button" onClick={() => { setEditingProvider(null); setShowProviderModal(true); }}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
              <span>Add Provider</span>
            </button>
          )}
        </div>

        {/* My Preferences summary */}
        {hasProviders && (
          <div className="gwCardSection" data-gw-card="ai_preferences">
            <div className="gwSectionTitle" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>My Preferences</span>
              <button className="gwGhostBtn" type="button" onClick={() => setShowPrefModal(true)}
                disabled={!canConfigure}
                title={canConfigure ? undefined : "Requires configure permission"}
                style={{ height: 28, fontSize: 12 }}>
                {pref?.configured ? "Edit" : "Customize"}
              </button>
            </div>
            <div className="gwCardSectionBody">
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 11, color: "var(--gw-muted)", marginBottom: 4 }}>Provider</div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>
                    {providerLabel(effectiveProvider) || "None"}
                    {!pref?.configured && effectiveProvider && <span style={{ fontSize: 11, color: "var(--gw-muted)" }}> (default)</span>}
                  </div>
                </div>
                {!isManagedProvider(effectiveProvider) && (
                  <div>
                    <div style={{ fontSize: 11, color: "var(--gw-muted)", marginBottom: 4 }}>Model</div>
                    <div style={{ fontSize: 14, fontWeight: 500, fontFamily: "monospace" }}>
                      {effectiveModel || <span style={{ color: "var(--gw-muted)" }}>Not set</span>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!hasProviders && (
          <div className="gwEmptyState">
            <div className="gwEmptyTitle">No AI providers yet</div>
            <div className="gwEmptyBody">Add an AI provider (OpenAI, Anthropic, Google, xAI, Ollama, or custom) to enable the editor's AI assistant.</div>
            {isAdmin && (
              <div className="gwEmptyActions">
                <button className="gwPrimaryBtn" type="button" onClick={() => { setEditingProvider(null); setShowProviderModal(true); }}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
                  <span>Add Provider</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Provider list */}
        {hasProviders && (
          <div className="gwCardSection" data-gw-card="ai_providers">
            <div className="gwSectionTitle">Providers</div>
            <div className="gwCardSectionBody" style={{ padding: 0 }}>
              {providers.map((p) => (
                <div key={p.id} className="umUserRow"
                  data-gw-resource="ai_providers" data-gw-id={p.id} data-gw-name={p.name}
                  style={{ cursor: isAdmin && !isManagedProvider(p) ? "pointer" : "default" }}
                  onClick={() => {
                    if (!isAdmin || isManagedProvider(p)) return;
                    setEditingProvider(p);
                    setShowProviderModal(true);
                  }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                    background: "rgba(156,120,255,0.1)", border: "1px solid rgba(156,120,255,0.2)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 700, color: "rgba(156,120,255,0.9)",
                  }}>
                    AI
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span className="umUserName">{providerLabel(p)}</span>
                      {p.is_default && (
                        <span style={{
                          fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5, padding: "2px 6px",
                          borderRadius: 4, background: "rgba(76,175,80,0.12)", color: "rgba(76,175,80,0.95)",
                          border: "1px solid rgba(76,175,80,0.2)", fontWeight: 600,
                        }}>Default</span>
                      )}
                      {(!isManagedProvider(p) || !isProviderReady(p)) && (
                        <span style={{
                          fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5, padding: "2px 6px",
                          borderRadius: 4, fontWeight: 600,
                          background: isProviderReady(p) ? "rgba(76,175,80,0.12)" : "rgba(244,67,54,0.12)",
                          color: isProviderReady(p) ? "rgba(76,175,80,0.95)" : "rgba(244,67,54,0.95)",
                          border: `1px solid ${isProviderReady(p) ? "rgba(76,175,80,0.2)" : "rgba(244,67,54,0.2)"}`,
                        }}>
                          {isManagedProvider(p)
                            ? "Unavailable"
                            : (isProviderReady(p) ? (p.api_key_set ? "Key Set" : "Ready") : "No Key")}
                        </span>
                      )}
                    </div>
                    <div className="umUserMeta">
                      {isManagedProvider(p) ? (
                        <>
                          <span>Managed by Control Seat</span>
                          {isProviderReady(p) && usage && (
                            <>
                              <span>{` \u00b7 ${formatMicrousd(usage.used_microusd)} used \u00b7 ${formatMicrousd(usage.remaining_microusd)} remaining`}</span>
                              <br />
                              <span>{`Resets ${formatResetDate(usage.reset_at)} UTC`}</span>
                            </>
                          )}
                          {isProviderReady(p) && usageLoaded && !usage && <span> \u00b7 Usage unavailable</span>}
                        </>
                      ) : (
                        <>{p.endpoint}{p.default_model && ` \u00b7 ${p.default_model}`}</>
                      )}
                    </div>
                  </div>
                  {isAdmin && (!isManagedProvider(p) || (!p.is_default && isProviderReady(p))) && (
                    <button type="button" className="gwIconButton" style={{ flexShrink: 0 }}
                      onClick={(e) => openMenu(e, p.id)}>
                      <img src={moreIcon} alt="More" className="gwIcon" style={{ width: 16, height: 16 }} />
                    </button>
                  )}
                  {menuOpen === p.id && (
                    <div className="gwProjectMenu" style={menuStyle} onClick={(e) => e.stopPropagation()}>
                      {!isManagedProvider(p) && (
                        <button className="gwProjectMenuItem" type="button"
                          onClick={() => { setMenuOpen(null); setEditingProvider(p); setShowProviderModal(true); }}>
                          Edit
                        </button>
                      )}
                      {!p.is_default && isProviderReady(p) && (
                        <button className="gwProjectMenuItem" type="button"
                          onClick={() => handleSetDefault(p)}>
                          Set as Default
                        </button>
                      )}
                      {!isManagedProvider(p) && (
                        <>
                          <div className="gwProfileDivider" />
                          <button className="gwProjectMenuItem gwProjectMenuDanger" type="button"
                            onClick={() => { setMenuOpen(null); setDeleteTarget(p); }}>
                            Remove
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {!isAdmin && <span className="gwHint">Only administrators can manage AI providers.</span>}
      </section>

      {/* Provider modal */}
      {showProviderModal && !isManagedProvider(editingProvider) && (
        <ProviderModal
          editing={editingProvider}
          prefilled={proposedProvider}
          onClose={() => { setShowProviderModal(false); setEditingProvider(null); setProposedProvider(null); }}
          onSaved={fetchData}
          providerCount={providers.length}
        />
      )}

      {/* Preferences modal */}
      {showPrefModal && (
        <PreferencesModal
          providers={providers}
          pref={pref}
          onClose={() => setShowPrefModal(false)}
          onSaved={fetchData}
        />
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="gwOverlay" onClick={() => setDeleteTarget(null)}>
          <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 400 }}>
            <div className="gwModalHeader">
              <div>
                <div className="gwModalTitle">Remove Provider</div>
                <div className="gwModalSubtitle">This action cannot be undone.</div>
              </div>
              <button className="gwModalClose" type="button" onClick={() => setDeleteTarget(null)}>
                <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
              </button>
            </div>
            <div className="gwModalBody">
              <p style={{ fontSize: 13, color: "var(--gw-text-secondary)", margin: 0 }}>
                Are you sure you want to remove <strong>{deleteTarget.name}</strong>? Users relying on this provider will need to reconfigure.
              </p>
            </div>
            <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
              <button className="gwGhostBtn" type="button" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="gwDangerBtn" type="button" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Removing..." : "Remove Provider"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { AiSettingsPage };
